<template>
  <div>
    <EUIApp :loading="loading" />
    <GlobalError />
  </div>
</template>

<script>
import {EUIApp} from 'eclipx.ui';
import {mapMutations} from 'vuex';
import GlobalError from './components/GlobalError/GlobalError';

import {onLogin} from './vue-apollo';

import QueryUser from './graphql/queries/QueryUser';

/**
 * This component handles fetching a user one the authentication service has finished requesting data.
 * The authentication service email will be used to query for the users details.
 *
 * Before a user is loaded, a full page loading screen will be displayed.
 */
export default {
  name: 'DragonballApp',
  computed: {
    user: function () {
      return this.$store.getters['auth/user'];
    },
    // Show an application loading state while the authentication service is in progress or there is no fetched
    // application user
    loading: function () {
      if (this.$route.meta.skipAuthentication) {
        return false;
      }
      return !this.user || this.$auth.loading;
    }
  },
  watch: {
    // When this component first loads it is likely the authentication service is still fetching data.
    // Only call to get a user when the authentication service has finished
    '$auth.loading': function (loading) {
      if (!loading) {
        this.getUser();
      }
    }
  },
  created() {
    // If the authentication service has finished fetching data before the component was created, we can fetch a user
    if (!this.$auth.loading) {
      this.getUser();
    }
  },
  methods: {
    ...mapMutations({setUser: 'auth/setUser', clearUser: 'auth/clearUser'}),
    async getUser() {
      // If the authentication is invalid, kick the user back to the login page
      if (!this.$auth.isAuthenticated) {
        return;
      }

      try {
        const token = await this.$auth.getIdTokenClaims();
        onLogin(this.$apollo, token.__raw);

        // Do not fetch a user if the route does not require authentication
        // Do not fetch a user if there is one with the same email as the authentication service
        if (
          this.$route.meta.skipAuthentication ||
          (this.user && this.user.email === this.$auth.user.email)
        ) {
          return;
        }

        // Prevent the loading state from being removed while the new user request is in progress
        this.clearUser();

        // Fetch details of the authenticated user
        let query = await this.$apollo.query({
          query: QueryUser,
          variables: {
            email: this.$auth.user.email,
            appCode: process.env.VUE_APP_CODE
          }
        });
        this.setUser(query.data.user);
      } catch (e) {
        // Log out the authentication service if there was an error getting the user
        this.$router.push({name: 'logout'});
      }
    }
  },
  components: {
    EUIApp,
    GlobalError
  }
};
</script>
